/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Box, Typography, IconButton, Divider } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CloseIcon from '@mui/icons-material/Close';
import { CallDirection, CallStatus } from '../typings';
import { getCallIConsAndStyles } from '../utils/get-call-icons-and-styles';
import wabaLogo from '../../../shared/assets/svg/waba-call-field-icon.svg';
import TypographyWithEllipsisTooltip from '../../templates/components/template-table/typography-with-ellipsis';
import ChannelIcon from '../../../shared/icons/icons-revamp/channel_icon';
import ArrowExpandIcon from '../../../shared/icons/ArrowExpand';

interface CallHeaderProps {
  state: CallStatus;
  direction?: CallDirection;
  wabaLabel: string;
  wabaNumber: string;
  onClose?: () => void;
  onMinimize?: () => void;
}

const getCallHeaderActions = (state: CallStatus, direction?: CallDirection) => {
  switch (state) {
    case CallStatus.INITIATED:
      return {
        iconColor: '#7a7a7a',
        closeText: direction === CallDirection.INCOMING ? 'Ignore' : '',
        closeIcon: <ArrowExpandIcon color="#7a7a7a" size={12} />,
      };
    case CallStatus.IN_PROGRESS:
    case CallStatus.Connecting:
    case CallStatus.Ringing:
      return {
        iconColor: '#fff',
        closeText: 'Hide',
        closeIcon: <ArrowExpandIcon color="#fff" size={12} />,
      };
    case CallStatus.MISSED:
    case CallStatus.FAILED:
      return {
        iconColor: 'rgba(122, 122, 122, 1)',
        closeText: '',
        closeIcon: <CloseIcon sx={{ color: '#5f6368' }} />,
      };
    default:
      return {
        iconColor: 'rgba(122, 122, 122, 1)',
        closeText: '',
        closeIcon: null,
      };
  }
};

export const CallHeader = ({ state, direction, wabaLabel, wabaNumber, onClose, onMinimize }: CallHeaderProps) => {
  const { bg, titleColor, iconColor, icon, label, wabaLabelColor } = getCallIConsAndStyles(
    state,
    24,
    direction || CallDirection.INCOMING
  );

  const { iconColor: wabaIconColor, closeText, closeIcon } = getCallHeaderActions(state, direction);

  const getClickHandler = (state: CallStatus, onClose?: () => void, onMinimize?: () => void) => {
    const closeStates = [CallStatus.MISSED, CallStatus.END, CallStatus.FAILED];
    return closeStates.includes(state) ? onClose : onMinimize;
  };

  return (
    <Box
      css={css`
        background-color: ${bg};
        border-top-left-radius: 12px;
        border-top-right-radius: 12px;
      `}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" padding="16px 12px 12px 16px">
        <Box display="flex" alignItems="center" gap="8px">
          <Box sx={{ color: iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
          <Box display="flex" flexDirection="column">
            <Box display="flex" alignItems="center" gap="6px">
              <Typography fontSize="14px" fontWeight={[CallStatus.END].includes(state) ? 600 : 500} color={titleColor}>
                {label}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap="8px">
              <Box display="flex" gap={'4px'} alignItems="center" sx={{ color: titleColor }}>
                <ChannelIcon width={16} height={16} color={wabaIconColor} />
                <TypographyWithEllipsisTooltip
                  maxWidth={'320px'}
                  sx={{
                    color: `${wabaLabelColor} !important`,
                    fontSize: '12px !important',
                    fontWeight: '500 !important',
                  }}
                >
                  {wabaLabel ? wabaLabel : wabaNumber}
                </TypographyWithEllipsisTooltip>
              </Box>
            </Box>
          </Box>
        </Box>

        <IconButton
          size="small"
          sx={{
            border: closeText ? '1px solid rgba(224, 225, 228, 1)' : 'none',
            borderRadius: '38px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            padding: closeText ? '8px 8px 8px 12px' : '8px',
          }}
          onClick={getClickHandler(state, onClose, onMinimize)}
          disabled={state === CallStatus.END}
        >
          {closeText && (
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '12px',
                color: wabaLabelColor === 'rgba(122, 122, 122, 1)' ? '#2d2d2d' : wabaLabelColor,
              }}
            >
              {closeText}
            </Typography>
          )}
          {closeIcon}
        </IconButton>
      </Box>
      {state === CallStatus.END && <Divider variant="fullWidth" sx={{ borderColor: '#e0e0e0' }} />}
    </Box>
  );
};
